/**
 * Traduction minimale des messages du namespace `ai_poll`, SANS i18next.
 *
 * Le module `ai-poll` doit tourner dans une fonction Vercel Node (serveur MCP)
 * comme dans le navigateur : on ne peut pas y dépendre de l'instance i18next
 * de l'app (détection navigateur, React). On lit donc directement les mêmes
 * fichiers JSON que le front (`packages/core/src/i18n/locales/<lang>/ai_poll.json`)
 * — une seule source de vérité pour les textes, vérifiée par
 * `scripts/check-i18n-parity.mjs` comme tout autre namespace.
 *
 * Le front, lui, doit utiliser `t("ai_poll:<messageKey>", params)` : le
 * namespace est enregistré dans `i18n/index.ts`. Cette fonction ne sert qu'aux
 * consommateurs sans i18next (serveur MCP, tests).
 *
 * Pluriel : même règle qu'i18next v26 (`_one` / `_other`, choisis par
 * `Intl.PluralRules`), avec repli sur `_other` pour les catégories absentes
 * (`many` en fr/es pour les très grands nombres).
 */
import en from "../../i18n/locales/en/ai_poll.json" with { type: "json" };
import fr from "../../i18n/locales/fr/ai_poll.json" with { type: "json" };
import es from "../../i18n/locales/es/ai_poll.json" with { type: "json" };

export type AiPollLanguage = "fr" | "en" | "es";
export const AI_POLL_LANGUAGES: readonly AiPollLanguage[] = ["fr", "en", "es"];
export const AI_POLL_DEFAULT_LANGUAGE: AiPollLanguage = "en";

export type MessageParams = Record<string, string | number>;

const BUNDLES: Record<AiPollLanguage, unknown> = { en, fr, es };

export function isAiPollLanguage(value: unknown): value is AiPollLanguage {
  return (
    typeof value === "string" &&
    (AI_POLL_LANGUAGES as readonly string[]).includes(value)
  );
}

/** Tag BCP-47 pour Intl (même correspondance que `localeTag` de i18n/index). */
export function languageTag(language: AiPollLanguage): string {
  if (language === "en") return "en-US";
  if (language === "es") return "es-ES";
  return "fr-FR";
}

function lookup(bundle: unknown, key: string): string | undefined {
  let node: unknown = bundle;
  for (const part of key.split(".")) {
    if (!node || typeof node !== "object") return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === "string" ? node : undefined;
}

function interpolate(template: string, params: MessageParams = {}): string {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name)
      ? String(params[name])
      : match,
  );
}

/**
 * Rend `ai_poll:<key>` dans la langue demandée. Repli : anglais, puis la clé
 * elle-même (jamais d'exception — un message manquant ne doit pas masquer
 * l'erreur qu'il décrit).
 */
export function translateAiPoll(
  key: string,
  language: AiPollLanguage = AI_POLL_DEFAULT_LANGUAGE,
  params: MessageParams = {},
): string {
  const candidates: string[] = [];
  if (typeof params.count === "number") {
    const category = new Intl.PluralRules(languageTag(language)).select(
      params.count,
    );
    candidates.push(`${key}_${category}`, `${key}_other`);
  }
  candidates.push(key);

  for (const lang of [language, AI_POLL_DEFAULT_LANGUAGE]) {
    for (const candidate of candidates) {
      const template = lookup(BUNDLES[lang], candidate);
      if (template !== undefined) return interpolate(template, params);
    }
  }
  return key;
}
